import { useMemo } from 'react'

import {
  matchFilterString, sortArrayBy,
} from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'
import type { UseLoadMorePayload } from '@domain-group/react-hooks'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import { ServiceCategoryModel } from '@models/types'

import type { EntityOptions } from '@redux/modules/types'

const watchEntityKeys = ['serviceCategories']

type ServiceCategoriesFilters = {
  service_key?: string,
  string?: string,
}

interface UseEntitiesOptions {
  entityKey?: string,
  filters?: ServiceCategoriesFilters,
  performHttpRequests?: boolean,
  requestOptions?: EntityOptions,
}

const useServiceCategories = (options: UseEntitiesOptions = {}) => {
  const { filters = {}, requestOptions } = options
  const {
    service_key: filterServiceKey,
    string: filterString,
  } = filters

  const {
    updatedEntities: { serviceCategories: serviceCategoriesUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const entities = useSelector(reduxState => reduxState.entities)
  const {
    serviceCategories,
    services,
  } = entities

  const filteredServiceCategories = useMemo((): ServiceCategoryModel[] => {
    const filtered = Object.values(serviceCategories).filter((serviceCategory) => {
      const { id, service_id, title } = serviceCategory

      const service = service_id && services[service_id] ? services[service_id] : {}

      // @ts-expect-error TS2339
      const matchServiceKey = filterServiceKey ? service.short_name === filterServiceKey : true

      // String match (ID & Title)
      const matchId = id.toString() === filterString
      const matchTitle = matchFilterString(title, filterString)
      const matchString = matchId || matchTitle

      return matchServiceKey && matchString
    })

    return sortArrayBy(filtered, 'asc', 'title')
  }, [serviceCategoriesUpdatedAt, JSON.stringify(filters)])

  const filteredServiceCategoriesCount = filteredServiceCategories.length
  const hasFilteredServiceCategories = !!filteredServiceCategories.length

  const loadMorePayload: UseLoadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredServiceCategoriesCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingServiceCategories } = useReduxAction(
    'serviceCategories',
    'loadServiceCategories',
    {
      ...filtersWithOffset,
      ...requestOptions,
      limit,
    },
    [performHttpRequests, filtersWithOffset],
    {
      // @ts-expect-error TS2339
      shouldPerformFn: ({ loading }) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredServiceCategories,
    filteredServiceCategoriesCount,
    hasFilteredServiceCategories,
    loading: loadingServiceCategories,
  }
}

export default useServiceCategories
